import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Table as TableIcon } from 'lucide-react';

const fmt$ = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

function buildSchedule(principal, annualRate, years) {
  const n = Math.max(1, Math.round(years * 12));
  const r = (annualRate / 100) / 12;
  const rows = [];
  if (!principal || principal <= 0) return rows;
  const pmt = r === 0 ? principal / n : (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  let balance = principal;
  for (let m = 1; m <= n; m++) {
    const interest = balance * r;
    let principalPaid = pmt - interest;
    // Guard against tiny negative on the last row from FP rounding
    if (m === n) principalPaid = balance;
    balance = Math.max(0, balance - principalPaid);
    rows.push({ month: m, payment: pmt, principal: principalPaid, interest, balance });
  }
  return rows;
}

/**
 * AmortizationSchedule
 * @param {number} principal – Loan amount (post-down-payment).
 * @param {number} rate      – Annual interest rate (%). e.g. 6.49
 * @param {number} years     – Loan term in years.
 * @param {string} [title]   – Optional heading override.
 * @param {string} [testId]  – Optional data-testid prefix.
 */
export default function AmortizationSchedule({ principal, rate, years, title = 'Amortization Schedule', testId = 'amortization' }) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => buildSchedule(principal, rate, years), [principal, rate, years]);

  if (!rows.length) return null;

  const totalInterest = rows.reduce((s, r) => s + r.interest, 0);
  const totalPaid = rows.reduce((s, r) => s + r.payment, 0);

  return (
    <div className="mt-6" data-testid={`${testId}-wrapper`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`${testId}-toggle`}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition shadow-sm print:hidden"
      >
        <span className="flex items-center gap-2.5 font-semibold text-sm" style={{ color: '#070B5F' }}>
          <TableIcon size={16} style={{ color: '#6BA82E' }} />
          {open ? 'Hide' : 'Show'} Amortization Schedule
          <span className="hidden sm:inline text-xs font-normal text-slate-400">
            · {rows.length} months · {years}-yr term
          </span>
        </span>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      {open && (
        <div className="mt-4 rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden" data-testid={`${testId}-table-container`}>
          {/* Summary strip */}
          <div className="grid grid-cols-3 gap-2 p-4 bg-slate-50 border-b border-slate-100 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Total Payments</div>
              <div className="font-serif font-bold text-base sm:text-lg mt-0.5" style={{ color: '#070B5F' }}>{fmt$(totalPaid)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Total Interest</div>
              <div className="font-serif font-bold text-base sm:text-lg mt-0.5" style={{ color: '#f59e0b' }}>{fmt$(totalInterest)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Principal</div>
              <div className="font-serif font-bold text-base sm:text-lg mt-0.5" style={{ color: '#6BA82E' }}>{fmt$(principal)}</div>
            </div>
          </div>

          {/* Scrollable table */}
          <div className="max-h-[560px] overflow-auto print:max-h-none print:overflow-visible" data-testid={`${testId}-scroll`}>
            <table className="w-full text-xs sm:text-sm">
              <thead className="sticky top-0 z-10 bg-white border-b border-slate-200">
                <tr className="text-left uppercase tracking-widest text-[10px] text-slate-500">
                  <th className="py-2.5 px-3 sm:px-4">Mo.</th>
                  <th className="py-2.5 px-2 sm:px-4 text-right">Payment</th>
                  <th className="py-2.5 px-2 sm:px-4 text-right">Principal</th>
                  <th className="py-2.5 px-2 sm:px-4 text-right">Interest</th>
                  <th className="py-2.5 px-3 sm:px-4 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.month}
                    className={`border-b border-slate-50 ${i % 12 === 11 ? 'bg-slate-50/60' : ''}`}
                    data-testid={`${testId}-row-${r.month}`}
                  >
                    <td className="py-2 px-3 sm:px-4 font-semibold" style={{ color: '#070B5F' }}>{r.month}</td>
                    <td className="py-2 px-2 sm:px-4 text-right font-mono text-slate-700 whitespace-nowrap">{fmt$(r.payment)}</td>
                    <td className="py-2 px-2 sm:px-4 text-right font-mono whitespace-nowrap" style={{ color: '#6BA82E' }}>{fmt$(r.principal)}</td>
                    <td className="py-2 px-2 sm:px-4 text-right font-mono text-slate-500 whitespace-nowrap">{fmt$(r.interest)}</td>
                    <td className="py-2 px-3 sm:px-4 text-right font-mono font-semibold whitespace-nowrap" style={{ color: '#070B5F' }}>{fmt$(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-400 text-center py-2 border-t border-slate-100 sm:hidden">
            Swipe horizontally if columns are cut off.
          </div>
        </div>
      )}
    </div>
  );
}
